/**
 * `POST /internal/team-standing/report` — the Apple team-standing sweep's
 * callback (Track I, U44).
 *
 * WHY THERE IS AN ENDPOINT AT ALL. The sweep runs inside the one-shot browser
 * task, which by D1b holds no `DATABASE_URL` and carries no AWS SDK — it reaches
 * the platform over HTTP and nothing else. So the runner READS the portal and
 * the backend OWNS the ledger: the runner never decides what a reading means,
 * never learns which teams we expect to see, and never writes a row.
 *
 * That split is also what makes membership-removal detectable. The runner cannot
 * know a team is missing, because it only ever sees what the selector showed it;
 * the backend compares a PROVEN-COMPLETE walk against the `PublishingAccount`
 * rows and decides. An unprovable walk yields `stale_check` for the unread
 * teams, which is the fail-closed direction the carried Track H rule requires.
 *
 * THE REQUEST IS STRICT ON PURPOSE. `ObservedStandingValuesSchema` rejects any
 * key outside U44's persist-allowlist, so a runner that started reading the
 * street address or phone number off Membership Details would be refused at the
 * boundary rather than quietly widening what the platform holds.
 */

import { z } from 'zod'
import {
  ObservedStandingFieldProvenanceSchema,
  ObservedStandingFieldSchema,
  ObservedStandingValuesSchema,
  TeamStandingFindingSchema
} from '../../schemas/entities/publishing-account'

/**
 * Why one team could not be read.
 *
 * `selector_miss` is the page-level anchor missing — Membership Details did not
 * render at all, which is a finding in its own right and the failure mode U29
 * says this codebase has shipped three times. `read_failed` is everything else
 * that went wrong on that team's page, including a session that died mid-walk.
 * Neither ever clears a prior reading.
 */
export const TeamReadFailureReasonSchema = z.enum(['selector_miss', 'read_failed'])
export type TeamReadFailureReason = z.infer<typeof TeamReadFailureReasonSchema>

const ObservedTeamBaseSchema = z.object({
  outcome: z.literal('observed'),
  /** Apple team id, e.g. `V7AVGAR3HQ`. */
  accountId: z.string().min(1).max(64),
  /** The team's display name as the selector showed it. Corroboration only. */
  displayName: z.string().max(255).optional(),
  values: ObservedStandingValuesSchema,
  /**
   * Fields with no meaning for this enrollment kind or role — entity name on a
   * personal enrollment, the account holder where our role is not Admin.
   * NOT-APPLICABLE IS NOT MISSING: naming them here is what stops a NULL that is
   * expected by design from raising a finding no later run could ever clear.
   */
  notApplicableFields: z.array(ObservedStandingFieldSchema).default([]),
  /**
   * Optional. Absent means every value present was produced by a deterministic
   * selector, which the backend stamps `scraped`. The deferred interpreter tier
   * is what will start sending this explicitly, per field.
   */
  provenance: ObservedStandingFieldProvenanceSchema.optional()
})

/**
 * A field cannot be both READ and NOT-APPLICABLE, and the contradiction is
 * refused rather than resolved.
 *
 * Resolving it means picking, and either pick is a lie the row then carries: let
 * the value win and a field declared meaningless is stored anyway; let
 * not-applicable win and a real reading is thrown away and NULLed. Neither is
 * recoverable afterwards, because the row no longer records that the two
 * disagreed. A runner sending both is malformed — say so at the boundary.
 */
const ObservedTeamSchema = ObservedTeamBaseSchema.refine(
  team => !team.notApplicableFields.some(field => team.values[field] !== undefined),
  { message: 'a field cannot be both read and declared not-applicable', path: ['notApplicableFields'] }
)

const UnreadTeamSchema = z.object({
  outcome: z.literal('unread'),
  accountId: z.string().min(1).max(64),
  displayName: z.string().max(255).optional(),
  reason: TeamReadFailureReasonSchema,
  /** One line, written by the runner. Never a copy of page content. */
  detail: z.string().max(500)
})

/**
 * A plain union rather than a discriminated one: `ObservedTeamSchema` carries a
 * cross-field refinement, which Zod's discriminated union does not accept. The
 * `outcome` literals still make the two branches unambiguous.
 */
export const TeamStandingTeamReportSchema = z.union([ObservedTeamSchema, UnreadTeamSchema])
export type TeamStandingTeamReport = z.infer<typeof TeamStandingTeamReportSchema>

/**
 * Whether the walk saw ALL the teams our Apple ID can reach.
 *
 * `complete: false` is not a degraded success — it disables membership-removal
 * detection entirely for this sweep, because absence from an unprovable walk is
 * indistinguishable from an intermittent one.
 */
export const TeamEnumerationSchema = z.object({
  complete: z.boolean(),
  /** Required when `complete` is false: what stopped the walk being provable. */
  reason: z.string().max(500).optional(),
  /** How many teams the selector listed. Zero with `complete: true` is refused. */
  teamCount: z.number().int().nonnegative(),
  /**
   * Present when the sweep was deliberately narrowed to named teams — the shape
   * the first supervised run takes, and any targeted re-check afterwards.
   *
   * It exists to stop a targeted probe DEGRADING the fleet. Without it, reading
   * one team would leave every other team unread, and "unread" writes
   * `stale_check` — so a one-team check would knock nine healthy readings out of
   * `observed`. With it, the platform considers only these accounts and leaves
   * the rest of the ledger exactly as it found it.
   */
  restrictedTo: z.array(z.string().min(1).max(64)).optional()
})
export type TeamEnumeration = z.infer<typeof TeamEnumerationSchema>

export const TeamStandingSweepReportSchema = z.object({
  /** Runner-generated, for correlating the sweep with its task logs. */
  sweepId: z.string().min(1).max(64),
  startedAt: z.string(),
  completedAt: z.string(),
  enumeration: TeamEnumerationSchema,
  teams: z.array(TeamStandingTeamReportSchema)
})
export type TeamStandingSweepReport = z.infer<typeof TeamStandingSweepReportSchema>

export const TeamStandingSweepResultSchema = z.object({
  sweepId: z.string(),
  /** Teams whose reading was recorded. */
  accountsObserved: z.number().int().nonnegative(),
  /** Teams recorded `stale_check` — read attempted or expected, nothing usable. */
  accountsStale: z.number().int().nonnegative(),
  /**
   * Teams the portal showed that no `PublishingAccount` row describes.
   *
   * REPORTED, NEVER REGISTERED — the same rule `registration-drift.ts` follows. A
   * record created here would carry `unanswered` for everything an operator
   * actually needs to answer and would then look registered forever.
   */
  accountsUnknownToRegistry: z.array(z.string()),
  findings: z.array(TeamStandingFindingSchema)
})
export type TeamStandingSweepResult = z.infer<typeof TeamStandingSweepResultSchema>
